import { useState } from 'react'
import { Link } from 'react-router-dom'
import { setSubscriptionCapway } from '@/api/customers'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { canEnableInvoicing } from '@/lib/customer'
import type { Customer, Subscription } from '@/api/types'

const ENABLED_LABEL = 'Fakturering på'
const DISABLED_LABEL = 'Fakturering av'
const ENABLE_BUTTON_LABEL = 'Aktivera fakturering'
const DISABLE_BUTTON_LABEL = 'Avaktivera fakturering'
const ENABLED_HINT = 'Skickade leveranser faktureras automatiskt via Capway vid nästa körning.'
const DISABLED_HINT = 'Aktivera så faktureras kundens skickade leveranser automatiskt via Capway.'
const VERIFY_HINT = 'Kunden måste vara verifierad innan fakturering kan aktiveras.'
const INVOICES_LINK_LABEL = 'Visa fakturor'
const ENABLE_CONFIRM_MESSAGE =
  'Vill du aktivera fakturering för kunden? Riktiga fakturor skapas hos Capway vid nästa körning – även för leveranser som redan har skickats.'
const DISABLE_CONFIRM_MESSAGE =
  'Vill du avaktivera fakturering för kunden? Nya leveranser faktureras inte. Fakturor som redan har skapats skickas ändå till Capway.'
const ERROR_MESSAGE = 'Kunde inte uppdatera faktureringsinställningen just nu.'

interface CustomerInvoicingCardProps {
  customer: Customer
  onSubscriptionChanged: (subscription: Subscription) => void
}

export function CustomerInvoicingCard({ customer, onSubscriptionChanged }: CustomerInvoicingCardProps): React.ReactElement {
  const [isSaving, setIsSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const subscription = customer.subscription
  const hasSubscription = subscription !== null
  const isEnabled = subscription?.enable_capway === true
  const canEnable = canEnableInvoicing(customer)
  const showButton = isEnabled || canEnable
  const showVerifyHint = hasSubscription && !isEnabled && !canEnable
  const hasInvoicesLink = customer.identity_number !== null
  const hasError = errorMessage !== null
  const statusLabel = isEnabled ? ENABLED_LABEL : DISABLED_LABEL
  const buttonLabel = isEnabled ? DISABLE_BUTTON_LABEL : ENABLE_BUTTON_LABEL
  const buttonVariant = isEnabled ? 'destructive' : 'default'
  const confirmMessage = isEnabled ? DISABLE_CONFIRM_MESSAGE : ENABLE_CONFIRM_MESSAGE
  const invoicesHref = `/invoices?search=${encodeURIComponent(customer.identity_number ?? '')}`

  const handleToggle = async (): Promise<void> => {
    if (!subscription) {
      return
    }

    const isConfirmed = window.confirm(confirmMessage)

    if (!isConfirmed) {
      return
    }

    setIsSaving(true)
    setErrorMessage(null)

    try {
      const updated = await setSubscriptionCapway(customer.id, !isEnabled)

      onSubscriptionChanged(updated)
    } catch {
      setErrorMessage(ERROR_MESSAGE)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Card className="shadow-none border-border/80">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>Fakturering</CardTitle>
          <CardDescription>{statusLabel}</CardDescription>
        </div>
        {showButton && (
          <Button
            variant={buttonVariant}
            className="shrink-0 rounded-none"
            disabled={isSaving}
            onClick={() => void handleToggle()}
          >
            {buttonLabel}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {isEnabled && <p className="text-sm text-muted-foreground">{ENABLED_HINT}</p>}
        {canEnable && <p className="text-sm text-muted-foreground">{DISABLED_HINT}</p>}
        {showVerifyHint && <p className="text-sm text-muted-foreground">{VERIFY_HINT}</p>}
        {hasInvoicesLink && (
          <Link
            to={invoicesHref}
            className="inline-block text-sm text-primary hover:underline"
          >
            {INVOICES_LINK_LABEL}
          </Link>
        )}
        {hasError && <p className="text-sm text-destructive">{errorMessage}</p>}
      </CardContent>
    </Card>
  )
}
